import React, { useCallback, useEffect, useState } from 'react';
import { Coins, Save, Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import MainLayout from '../../components/MainLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';

/**
 * SuperAdmin credits pricing config. Adjusts the global knobs that drive
 * credit costs (LLM rates + browser + PDF + markup + top-up minimum).
 */
export default function CreditsPricingPage() {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/super-admin/credits/pricing');
      setState(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'You must be a Super Admin to view this page');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (patch) => setState((s) => ({ ...s, pricing: { ...s.pricing, ...patch } }));
  const setRate = (model, key, val) => setState((s) => ({
    ...s,
    pricing: {
      ...s.pricing,
      model_rates: {
        ...s.pricing.model_rates,
        [model]: { ...s.pricing.model_rates[model], [key]: parseFloat(val) || 0 },
      },
    },
  }));

  const save = async () => {
    if (!state?.pricing) return;
    setSaving(true);
    try {
      const p = state.pricing;
      const { data } = await api.put('/super-admin/credits/pricing', {
        credit_usd_value: parseFloat(p.credit_usd_value) || 0.10,
        markup_multiplier: parseFloat(p.markup_multiplier) || 2.5,
        browser_usd_per_minute: parseFloat(p.browser_usd_per_minute) || 0.05,
        pdf_usd_each: parseFloat(p.pdf_usd_each) || 0.02,
        min_topup_usd: parseFloat(p.min_topup_usd) || 5,
        model_rates: p.model_rates,
      });
      setState(data);
      toast.success('Pricing updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (!state) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto p-6 text-sm text-slate-500 inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading pricing…
        </div>
      </MainLayout>
    );
  }

  const p = state.pricing || {};
  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto space-y-4 p-2" data-testid="credits-pricing-page">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Coins className="h-5 w-5 text-amber-500" /> Credits Pricing (SuperAdmin)
          </h1>
          <Badge variant="outline" className="rounded-none border-rose-400 text-rose-700 text-[10px]">GLOBAL</Badge>
          <Button
            className="rounded-none bg-emerald-600 hover:bg-emerald-700 ml-auto"
            onClick={save}
            disabled={saving}
            data-testid="pricing-save-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            Save
          </Button>
        </div>

        <div className="rounded-none border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 flex items-start gap-2">
          <Info className="h-4 w-4 mt-0.5 shrink-0 text-blue-600" />
          <span>
            Changes affect ALL orgs immediately. Credit cost per unit of AI usage is:
            <code className="mx-1 font-mono">raw_usd × markup / credit_usd_value</code>.
            Default: 1 credit = $0.10, markup 2.5×.
          </span>
        </div>

        {/* Core knobs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="pricing-core-grid">
          <Field label="1 credit = USD" value={p.credit_usd_value} onChange={(v) => set({ credit_usd_value: parseFloat(v) })} step="0.01" testid="pricing-credit-usd-value" />
          <Field label="Markup multiplier" value={p.markup_multiplier} onChange={(v) => set({ markup_multiplier: parseFloat(v) })} step="0.1" testid="pricing-markup" />
          <Field label="Browser USD / min" value={p.browser_usd_per_minute} onChange={(v) => set({ browser_usd_per_minute: parseFloat(v) })} step="0.01" testid="pricing-browser" />
          <Field label="PDF USD / doc" value={p.pdf_usd_each} onChange={(v) => set({ pdf_usd_each: parseFloat(v) })} step="0.01" testid="pricing-pdf" />
          <Field label="Min top-up USD" value={p.min_topup_usd} onChange={(v) => set({ min_topup_usd: parseFloat(v) })} step="1" testid="pricing-min-topup" />
        </div>

        {/* Model rates */}
        <h2 className="text-sm font-semibold text-slate-700 mt-4">LLM Rates (USD per 1M tokens)</h2>
        <div className="border rounded-none bg-white overflow-x-auto">
          <table className="w-full text-xs" data-testid="pricing-model-rates">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="p-2">Model</th>
                <th className="p-2 text-right">Input $/1M</th>
                <th className="p-2 text-right">Output $/1M</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(p.model_rates || {}).map(([m, r]) => (
                <tr key={m} className="border-t" data-testid={`pricing-model-${m}`}>
                  <td className="p-2 font-mono text-slate-700">{m}</td>
                  <td className="p-2 text-right">
                    <Input
                      type="number"
                      step="0.01"
                      value={r.input ?? 0}
                      onChange={(e) => setRate(m, 'input', e.target.value)}
                      className="rounded-none h-8 text-right w-28 ml-auto font-mono"
                      data-testid={`pricing-${m}-input`}
                    />
                  </td>
                  <td className="p-2 text-right">
                    <Input
                      type="number"
                      step="0.01"
                      value={r.output ?? 0}
                      onChange={(e) => setRate(m, 'output', e.target.value)}
                      className="rounded-none h-8 text-right w-28 ml-auto font-mono"
                      data-testid={`pricing-${m}-output`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Fixed top-up packages preview (read-only for now) */}
        <h2 className="text-sm font-semibold text-slate-700 mt-4">Top-up Packages (read-only)</h2>
        <div className="border rounded-none bg-white overflow-x-auto">
          <table className="w-full text-xs" data-testid="pricing-packages-table">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="p-2">Package</th>
                <th className="p-2 text-right">USD</th>
                <th className="p-2 text-right">Credits</th>
                <th className="p-2 text-right">Bonus</th>
                <th className="p-2">Tagline</th>
              </tr>
            </thead>
            <tbody>
              {(state.packages || []).map((pkg) => (
                <tr key={pkg.id} className="border-t" data-testid={`pricing-pkg-${pkg.id}`}>
                  <td className="p-2 font-semibold">{pkg.label}</td>
                  <td className="p-2 text-right font-mono">${pkg.usd.toFixed(2)}</td>
                  <td className="p-2 text-right font-mono">{pkg.credits}</td>
                  <td className="p-2 text-right font-mono text-emerald-700">+{pkg.bonus_credits}</td>
                  <td className="p-2 text-slate-500">{pkg.tagline}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-[11px] text-slate-500 p-2 border-t">
            Packages are hardcoded in <code>services/credits/packages.py</code> — edit there to add tiers.
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

function Field({ label, value, onChange, step, testid }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-600">{label}</label>
      <Input
        type="number"
        step={step}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-none mt-1 font-mono"
        data-testid={testid}
      />
    </div>
  );
}
